import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { PsHttpService, type PatSnapSuccessResponseData } from '@patsnap/tahiti-core';
import { coreTelecomApiUrlPrefix } from '@telecom/shared/domain-config';
import {
    PublicShareApi,
    type PublicShareApiRequest,
    type PublicShareApiResponse,
    type ShareAccessScope,
    type ShareAccessScopeResponse,
    type ShareInvitationBatchResponse,
    type ShareInvitationResultResponse,
} from '../public-share-api';
import type { CreatePublicShareLinkApi, RequestParams, ResponseData } from '../create-public-share-link-api';

@injectable()
export class PublicShareApiImpl extends PsHttpService
    implements PublicShareApi, CreatePublicShareLinkApi
{
    get options() {
        return {
            baseURL: coreTelecomApiUrlPrefix,
            headers: {
                'X-API-Version': '2.0',
            },
        };
    }

    async getShareInit(data: PublicShareApiRequest): Promise<PublicShareApiResponse> {
        const res = await this.post<PatSnapSuccessResponseData<PublicShareApiResponse>>({
            url: 'public-share/init',
            data,
        });
        return res.data;
    }

    async createPublicShareLink(data: RequestParams): Promise<ResponseData> {
        const res = await this.post<PatSnapSuccessResponseData<ResponseData>>({
            url: 'public-share',
            data: data.body,
        });
        return res.data;
    }

    async createInvitations(data: { share_id: string; emails: string[] }): Promise<ShareInvitationBatchResponse> {
        const res = await this.post<PatSnapSuccessResponseData<ShareInvitationBatchResponse>>({
            url: 'public-share/invitations',
            data,
        });
        return res.data;
    }

    async resendInvitation(data: {
        share_id: string;
        invitation_id: string;
    }): Promise<ShareInvitationResultResponse> {
        const res = await this.post<PatSnapSuccessResponseData<ShareInvitationResultResponse>>({
            url: 'public-share/invitations/resend',
            data,
        });
        return res.data;
    }

    async removeInvitation(data: { share_id: string; invitation_id: string }): Promise<boolean> {
        const res = await this.delete<PatSnapSuccessResponseData<boolean>>({
            url: 'public-share/invitations',
            data,
        });
        return res.data;
    }

    async updateAccessScope(data: {
        share_id: string;
        access_scope: ShareAccessScope;
    }): Promise<ShareAccessScopeResponse> {
        const res = await this.put<PatSnapSuccessResponseData<ShareAccessScopeResponse>>({
            url: 'public-share/access-scope',
            data,
        });
        return res.data;
    }
}
